import { z } from 'zod';

import { HomeResponse, HomeRowType } from '../catalog';
import { ApiOk, EntityId, I18nText, IsoDate } from '../primitives';
import { PublishStatus } from './playlists';

export const HomeItemRefType = z.enum(['hero', 'playlist', 'song']);
export type HomeItemRefType = z.infer<typeof HomeItemRefType>;

/** Which ref types a row type accepts. */
export const HOME_ROW_ITEM_TYPES: Record<HomeRowType, HomeItemRefType> = {
  heroes: 'hero',
  playlists: 'playlist',
  songs: 'song',
};

/** Rows and items the builder allows (docs/06 §2.7). */
export const HOME_MAX_ROWS = 30;
export const HOME_MAX_ITEMS_PER_ROW = 50;

export const CmsHomeItemDTO = z.object({
  refType: HomeItemRefType,
  refId: EntityId,
  position: z.number().int().nonnegative(),
  /** Denormalized for the builder UI. */
  title: z.string(),
  /** Artist line for a song, hero target title for a hero; `''` for a playlist. */
  subtitle: z.string(),
  /** Songs in a playlist item; null for the other types (the client localizes the label). */
  count: z.number().int().nonnegative().nullable(),
  imageUrl: z.string().nullable(),
  /**
   * False when the referenced entity is missing, deleted or still a draft. Such an item is kept
   * in the layout but dropped from `/home` and from the preview, so the builder warns about it.
   */
  published: z.boolean(),
});
export type CmsHomeItemDTO = z.infer<typeof CmsHomeItemDTO>;

export const CmsHomeRowDTO = z.object({
  id: EntityId,
  type: HomeRowType,
  title: I18nText,
  position: z.number().int().nonnegative(),
  status: PublishStatus,
  items: z.array(CmsHomeItemDTO),
});
export type CmsHomeRowDTO = z.infer<typeof CmsHomeRowDTO>;

/** `GET /cms/home` — the draft layout plus the last publish. */
export const CmsHomeResponse = z.object({
  rows: z.array(CmsHomeRowDTO),
  publishedAt: IsoDate.nullable(),
  /** Who ran the last `home.publish` (from `audit_log`), when it is known. */
  publishedBy: z.string().nullable(),
  /** True when a draft exists; false means `rows` is a copy of what is published. */
  hasDraft: z.boolean(),
  /** True when the draft differs from what TV/mobile currently see. */
  dirty: z.boolean(),
});
export type CmsHomeResponse = z.infer<typeof CmsHomeResponse>;

const HomeRowInput = z
  .object({
    /** Ignored for new rows; an existing draft row keeps its id. */
    id: EntityId.optional(),
    type: HomeRowType,
    title: I18nText,
    items: z
      .array(z.object({ refType: HomeItemRefType, refId: EntityId }))
      .max(HOME_MAX_ITEMS_PER_ROW),
  })
  .refine((row) => row.items.every((i) => i.refType === HOME_ROW_ITEM_TYPES[row.type]), {
    message: 'Row items must match the row type',
    path: ['items'],
  })
  .refine((row) => new Set(row.items.map((i) => i.refId)).size === row.items.length, {
    message: 'Duplicate items in a row',
    path: ['items'],
  });

/** `PUT /cms/home` — full ordered layout (rows + items), saved as draft. */
export const CmsHomePutBody = z.object({ rows: z.array(HomeRowInput).max(HOME_MAX_ROWS) });
export type CmsHomePutBody = z.infer<typeof CmsHomePutBody>;
export type CmsHomePutBodyInput = z.input<typeof CmsHomePutBody>;

export const CmsHomePutResponse = CmsHomeResponse;
export type CmsHomePutResponse = z.infer<typeof CmsHomePutResponse>;

/** `POST /cms/home/publish` */
export const CmsHomePublishResponse = ApiOk.extend({ publishedAt: IsoDate });
export type CmsHomePublishResponse = z.infer<typeof CmsHomePublishResponse>;

/** `GET /cms/home/preview` — same DTO as `/home`, rendered from the draft. */
export const CmsHomePreviewResponse = HomeResponse;
export type CmsHomePreviewResponse = z.infer<typeof CmsHomePreviewResponse>;
